import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, downloadFile, getToken } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  ScanLine, FileSignature, FolderLock, Landmark, Scale, Crown,
  ArrowRight, FileText, CalendarClock, CalendarPlus, AlertTriangle,
} from "lucide-react";

const TILES = [
  { to: "/app/scanner", icon: ScanLine, title: "Scanner un courrier", desc: "Analyser une photo", accent: true },
  { to: "/app/courriers", icon: FileSignature, title: "Rédiger un courrier", desc: "Recours, délai, remise…" },
  { to: "/app/coffre-fort", icon: FolderLock, title: "Coffre-fort", desc: "Vos justificatifs" },
  { to: "/app/impots", icon: Landmark, title: "Assistant Impôts", desc: "Cases & calendrier" },
  { to: "/app/droits", icon: Scale, title: "Vos droits", desc: "Infos & organismes" },
];

function UsageBar({ label, used, limit }) {
  const pct = Math.min(100, (used / limit) * 100);
  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium">{used}/{limit}</span>
      </div>
      <Progress value={pct} className="h-2" />
    </div>
  );
}

export default function Dashboard() {
  const { user, usage, limits, refresh } = useAuth();
  const [docs, setDocs] = useState([]);
  const [reminders, setReminders] = useState([]);

  useEffect(() => {
    refresh();
    api.get("/documents").then((r) => setDocs(r.data.slice(0, 3))).catch(() => {});
    api.get("/reminders").then((r) => setReminders(r.data.items)).catch(() => {});
  }, []); // eslint-disable-line

  const addToCalendar = (docId) => {
    downloadFile(`/documents/${docId}/ics?auth=${getToken()}`, "echeance_adminia.ics").catch(() => {});
  };

  return (
    <div className="space-y-6 adminia-fade-in">
      <div>
        <h1 className="font-heading font-bold text-2xl sm:text-3xl">
          Bonjour {user?.name?.split(" ")[0] || ""} 👋
        </h1>
        <p className="text-muted-foreground mt-1">Que souhaitez-vous faire aujourd'hui ?</p>
      </div>

      {/* Primary action */}
      <Card className="p-6 sm:p-8 bg-brand text-white border-none overflow-hidden relative">
        <div className="relative z-10 max-w-md">
          <h2 className="font-heading font-bold text-2xl mb-2">Un courrier vous stresse ?</h2>
          <p className="text-white/85 mb-5">
            Prenez-le en photo. En quelques secondes, vous saurez exactement quoi faire.
          </p>
          <Link to="/app/scanner">
            <Button className="rounded-full bg-white text-brand hover:bg-white/90 gap-2 h-12 px-6" data-testid="dashboard-scan-cta">
              <ScanLine className="h-5 w-5" /> Scanner maintenant
            </Button>
          </Link>
        </div>
        <ScanLine className="absolute -right-4 -bottom-6 h-40 w-40 text-white/10" />
      </Card>

      {/* Quick tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {TILES.map((t) => (
          <Link key={t.to} to={t.to} data-testid={`tile-${t.to.split("/").pop()}`}>
            <Card className="p-5 h-full hover:shadow-md hover:border-brand/40 transition-all duration-200 group">
              <div className="h-11 w-11 rounded-xl bg-brand/10 flex items-center justify-center mb-3 group-hover:bg-brand group-hover:text-white transition-colors">
                <t.icon className="h-6 w-6 text-brand group-hover:text-white transition-colors" />
              </div>
              <h3 className="font-heading font-semibold">{t.title}</h3>
              <p className="text-sm text-muted-foreground">{t.desc}</p>
            </Card>
          </Link>
        ))}
      </div>

      {/* Reminders */}
      {reminders.length > 0 && (
        <Card className="p-6" data-testid="reminders-card">
          <h3 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-brand" /> Rappels d'échéance
          </h3>
          <div className="space-y-2">
            {reminders.slice(0, 5).map((r) => (
              <div key={r.doc_id} className="flex items-center gap-3 p-3 rounded-lg bg-surface" data-testid={`reminder-${r.doc_id}`}>
                <div className={
                  r.status === "expired" ? "text-danger" : r.status === "soon" ? "text-amber-600" : "text-success"
                }>
                  {r.status === "ok" ? <CalendarClock className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm truncate">{r.titre}</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(r.date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                    {r.days_left >= 0 ? ` · dans ${r.days_left} j` : " · dépassée"}
                  </p>
                </div>
                <Button size="sm" variant="outline" className="rounded-full gap-1.5 shrink-0"
                  onClick={() => addToCalendar(r.doc_id)} data-testid={`reminder-ics-${r.doc_id}`}>
                  <CalendarPlus className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Calendrier</span>
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {/* Recent docs */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold text-lg">Analyses récentes</h3>
            <Link to="/app/scanner" className="text-sm text-brand font-medium inline-flex items-center gap-1">
              Nouveau <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          {docs.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              Aucun courrier analysé pour l'instant.
            </p>
          ) : (
            <div className="space-y-2">
              {docs.map((d) => (
                <Link key={d.id} to="/app/scanner" state={{ docId: d.id }}
                  className="flex items-center gap-3 p-3 rounded-lg bg-surface hover:bg-brand/5 transition-colors">
                  <FileText className="h-5 w-5 text-brand shrink-0" />
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{d.titre}</p>
                    <p className="text-xs text-muted-foreground truncate">{d.expediteur}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </Card>

        {/* Usage / plan */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold text-lg">
              {user?.is_premium ? "Votre abonnement" : "Votre offre gratuite"}
            </h3>
            {user?.is_premium ? (
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-success bg-success/10 px-3 py-1 rounded-full">
                <Crown className="h-3.5 w-3.5" /> Premium
              </span>
            ) : (
              <Link to="/app/abonnement">
                <Button size="sm" variant="outline" className="rounded-full border-brand text-brand gap-1">
                  <Crown className="h-3.5 w-3.5" /> Passer Premium
                </Button>
              </Link>
            )}
          </div>
          {user?.is_premium ? (
            <div className="flex items-start gap-3 text-sm text-muted-foreground">
              <CalendarClock className="h-5 w-5 text-success shrink-0 mt-0.5" />
              <p>Analyses, coffre-fort et courriers <strong className="text-foreground">illimités</strong>. Profitez de toutes les fonctionnalités.</p>
            </div>
          ) : usage && limits ? (
            <div className="space-y-4">
              <UsageBar label="Analyses ce mois" used={usage.analyses} limit={limits.analyses} />
              <UsageBar label="Documents au coffre" used={usage.vault} limit={limits.vault} />
              <UsageBar label="Courriers ce mois" used={usage.letters} limit={limits.letters} />
            </div>
          ) : null}
        </Card>
      </div>
    </div>
  );
}
